import { Route, Routes } from 'react-router-dom';
import useAuthContext from '../hooks/useAuthContext';
import { UserRole, UserType } from '../models/user';
import { CashFlowList } from '../pages/cash-flow-list';
import { EmployeeTransportList } from '../pages/employee-transport-list';
import { Home } from '../pages/home';
import { RewardCashFlowList } from '../pages/reward-cash-flow-list';
import { SalaryCashFlowList } from '../pages/salary-cash-flow-list';

import { BankHoursHistory } from '../pages/bank-hours-history';
import { BankHours } from '../pages/bank-hours-list';
import { CompanyCreate } from '../pages/company-create';
import { CompanyEdit } from '../pages/company-edit';
import { CompanyList } from '../pages/company-list';
import { DailyWorkCreate } from '../pages/daily-work-create';
import { DailyWorkList } from '../pages/daily-work-list';
import { EmployeePaySlip } from '../pages/employee-payslip';
import { EpiCatalog } from '../pages/epi-catalog';
import { EpiDeliveryList } from '../pages/epi-delivery-list';
import { EpiList } from '../pages/epi-list';
import { EpiMatrix } from '../pages/epi-matrix';
import { EpiReports } from '../pages/epi-reports';
import { EpiRounds } from '../pages/epi-rounds';
import { SectorDailyLogs } from '../pages/sector-daily-logs';
import { Login } from '../pages/login';
import { LoginAccessCodeBiometric } from '../pages/login-access-code-biometric';
import { PageNotFound } from '../pages/not-found';
import { RequestBankHoursList } from '../pages/request-bank-hours-list';
import { UserAccessCreate } from '../pages/user-access-create';
import { UserAccessEdit } from '../pages/user-access-edit';
import { UserCreate } from '../pages/user-create';
import { UserEdit } from '../pages/user-edit';
import { UserList } from '../pages/user-list';
import { WorkAlerts } from '../pages/work-alerts';
import { WorkScaleCreate } from '../pages/work-scale-create';
import { WorkScaleEdit } from '../pages/work-scale-edit';
import { WorkScaleList } from '../pages/work-scale-list';
import { NotificationsPanel } from '../pages/notifications';
import { AppRoutePaths } from '../utils/enums/routes';

export function Router() {
  const { user } = useAuthContext();

  function genericManagerRoutes() {
    return (
      <>
        <Route path={AppRoutePaths.HOME} element={<Home />} />
        <Route path={AppRoutePaths.USERS} element={<UserList />} />
        <Route path={AppRoutePaths.COMPANIES} element={<CompanyList />} />
        <Route path={AppRoutePaths.WORK_HISTORY} element={<DailyWorkList />} />
        <Route
          path={AppRoutePaths.EMPLOYEE_TRANSPORT}
          element={<EmployeeTransportList />}
        />
        <Route path={AppRoutePaths.BANK_HOURS} element={<BankHours />} />
        <Route
          path={AppRoutePaths.BANK_HOURS_HISTORY}
          element={<BankHoursHistory />}
        />
        <Route path={AppRoutePaths.EPIS} element={<EpiList />} />
        <Route path={AppRoutePaths.EPI_CATALOG} element={<EpiCatalog />} />
        <Route
          path={AppRoutePaths.EPI_DELIVERIES}
          element={<EpiDeliveryList />}
        />
        <Route path={AppRoutePaths.EPI_MATRIX} element={<EpiMatrix />} />
        <Route path={AppRoutePaths.EPI_ROUNDS} element={<EpiRounds />} />
        <Route path={AppRoutePaths.EPI_REPORTS} element={<EpiReports />} />
        <Route
          path={AppRoutePaths.SECTOR_DAILY_LOGS}
          element={<SectorDailyLogs />}
        />
        <Route
          path={AppRoutePaths.NOTIFICATIONS}
          element={<NotificationsPanel />}
        />
      </>
    );
  }

  function managerCreatorRoutes(role: UserRole) {
    return (
      role === UserRole.MANAGER_CREATOR && (
        <>
          <Route path={AppRoutePaths.USERS_CREATE} element={<UserCreate />} />
          <Route
            path={AppRoutePaths.COMPANIES_CREATE}
            element={<CompanyCreate />}
          />
          <Route
            path={AppRoutePaths.COMPANIES_EDIT}
            element={<CompanyEdit />}
          />
          <Route path={AppRoutePaths.USERS_EDIT} element={<UserEdit />} />
          <Route path={AppRoutePaths.CASH_FLOW} element={<CashFlowList />} />
          <Route
            path={AppRoutePaths.SALARY_CASH_FLOW}
            element={<SalaryCashFlowList />}
          />
          <Route
            path={AppRoutePaths.REWARD_CASH_FLOW}
            element={<RewardCashFlowList />}
          />
          <Route
            path={AppRoutePaths.BANK_HOURS_REQUEST}
            element={<RequestBankHoursList />}
          />
        </>
      )
    );
  }

  return (
    <Routes>
      <Route
        path={AppRoutePaths.ACCESS_CODE_BIOMETRIC_LOGIN}
        element={<LoginAccessCodeBiometric />}
      />
      <Route path={AppRoutePaths.BIOMETRIC_LOGIN} element={<Login />} />
      <Route
        path={AppRoutePaths.LOGIN}
        element={<LoginAccessCodeBiometric />}
      />
      <Route path={AppRoutePaths.NOT_FOUND} element={<PageNotFound />} />
      <Route
        path={AppRoutePaths.EMPLOYEE_PAYSLIP}
        element={<EmployeePaySlip />}
      />
      {user?.user.type === UserType.MASTER && (
        <>
          <Route path={AppRoutePaths.USERS_CREATE} element={<UserCreate />} />
          <Route
            path={AppRoutePaths.COMPANIES_CREATE}
            element={<CompanyCreate />}
          />
          <Route
            path={AppRoutePaths.COMPANIES_EDIT}
            element={<CompanyEdit />}
          />
          <Route path={AppRoutePaths.USERS_EDIT} element={<UserEdit />} />
          <Route path={AppRoutePaths.CASH_FLOW} element={<CashFlowList />} />
          <Route
            path={AppRoutePaths.SALARY_CASH_FLOW}
            element={<SalaryCashFlowList />}
          />
          <Route
            path={AppRoutePaths.REWARD_CASH_FLOW}
            element={<RewardCashFlowList />}
          />
          <Route
            path={AppRoutePaths.WORK_SCALE_LIST}
            element={<WorkScaleList />}
          />
          <Route
            path={AppRoutePaths.WORK_SCALE_CREATE}
            element={<WorkScaleCreate />}
          />
          <Route
            path={AppRoutePaths.WORK_SCALE_EDIT}
            element={<WorkScaleEdit />}
          />
          <Route path={AppRoutePaths.WORK_ALERTS} element={<WorkAlerts />} />
          <Route
            path={AppRoutePaths.ACCESS_CREATE}
            element={<UserAccessCreate />}
          />
          <Route
            path={AppRoutePaths.ACCESS_EDIT}
            element={<UserAccessEdit />}
          />
          <Route
            path={AppRoutePaths.BANK_HOURS_REQUEST}
            element={<RequestBankHoursList />}
          />
          {genericManagerRoutes()}
        </>
      )}
      {user?.user.type === UserType.MANAGER && (
        <>
          {genericManagerRoutes()}
          {managerCreatorRoutes(user.user.role)}
        </>
      )}
      {user?.user.type === UserType.USER && (
        <>
          <Route
            path={AppRoutePaths.WORK_HISTORY_CREATE}
            element={<DailyWorkCreate />}
          />
        </>
      )}
    </Routes>
  );
}
